import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useDispatch, useSelector } from "react-redux";
import { getCertificates } from "../../Slices/certificates";
import { useParams } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import cert_img from "../../assets/wzor_cert.png";
import { fontCalibri } from "../../assets/fonts/Calibri-normal.js";
import QRCode from "react-qr-code";
import dayjs from "dayjs";
import html2canvas from "html2canvas";


export default function SingleCertificate() {
  const { certificateId } = useParams();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth?.user);
  const certificatesState = useSelector((state) => state.certificates);

  const qrCodeRef = useRef(null);
  const certificateRef = useRef(null);
  const [certificateWidth, setCertificateWidth] = useState(900);

  // Rozmiary tekstu dla projektu o szerokości 900 px.
  const scaledFont = (size) => `${(size * certificateWidth) / 900}px`;

  const certificates = Array.isArray(certificatesState)
    ? certificatesState
    : Array.isArray(certificatesState?.certificates)
      ? certificatesState.certificates
      : Array.isArray(certificatesState?.data)
        ? certificatesState.data
        : [];

  const cert = certificates.find(
    (certificate) =>
      String(certificate?.id) === String(certificateId)
  );

  useEffect(() => {

    dispatch(getCertificates());
  }, [dispatch]);

  useLayoutEffect(() => {
    const element = certificateRef.current;
    if (!element) return;

    const updateWidth = (width) => {
      if (width > 0) setCertificateWidth(width);
    };

    updateWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      updateWidth(entry.contentRect.width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [cert?.id, user?.email]);

  // const certificateNumber = cert
  //   ? `SC-03-526-01/${String(cert.id).padStart(6, "0")}`
  //   : "";

  const getCertificateNumber = (cert) => {
  if (!cert) return "";

  const date = cert.issuanceDate
    ? dayjs(cert.issuanceDate)
    : null;

  const year = date?.isValid() ? date.format("YYYY") : "XXXX";
  const id = String(cert.id).padStart(6, "0");

  const code = String(cert.slug || "")
    .replace(/-/g, "")
    .slice(0, 8)
    .toUpperCase();

  return ["SC", year, id, code].filter(Boolean).join("-");
};

const certificateNumber = getCertificateNumber(cert);

  const firstName = cert?.user?.firstname || "";
  const lastName = cert?.user?.lastname || "";
  const fullName = `${firstName} ${lastName}`.trim();

  const qrValue = cert?.slug
    ? `${process.env.REACT_APP_MEDIA_URL}/public-badge/${cert.slug}`
    : "";



  const printDocument = () => {
    const input = document.getElementById("divToPrint");
    const qrImage = document.getElementById("qrCode");

    const scale = 2;
    const pdfWidth = 297;

    html2canvas(input, { useCORS: true, scale: scale }).then((canvas) => {
      const doc = new jsPDF("l", "mm");

      const externalImage = new Image();
      externalImage.src =
        "https://vcc-strapi-bucket.s3.eu-north-1.amazonaws.com/VCC_FUNDACJA_certyfikat_EUROPASS_2019_GREY_clear_faab51fa4b.png";
      doc.addImage(
        externalImage,
        "JPEG",
        0,
        0,
        doc.internal.pageSize.getWidth(),
        doc.internal.pageSize.getHeight(),
        null,
        "NONE"
      );

      const centerX = doc.internal.pageSize.getWidth() / 2;

      doc.addFileToVFS("Calibri.ttf", fontCalibri);
      doc.addFont("Calibri.ttf", "Calibri", "normal");
      doc.setFont("Calibri");

      doc.setFontSize(20);
      doc.setTextColor(0, 0, 0);

      doc.text(dayjs(cert.issuanceDate).format("DD.MM.YYYY"), centerX, 183, {
        align: "center",
      });

      doc.text(cert.badgeName, centerX, 113, {
        align: "center",
      });

      doc.text(
        dayjs(cert.exam_expiry_date).format("DD.MM.YYYY"),
        centerX,
        196,
        {
          align: "center",
        }
      );

      doc.text(`${cert.user.firstname} ${cert.user.lastname}`, centerX, 90, {
        align: "center",
      });

      doc.setFontSize(8);
      doc.text(`SC-03-526-01-${cert.id}`, 54, 131, { align: "center" });

      doc.addSvgAsImage(qrCodeRef.current.outerHTML, 31, 92, 33, 33);
      setTimeout(() => doc.save("certificate.pdf"), 500);
    });
  };

  if (!user?.email) {
    return (
      <main>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Typography
            sx={{
              color: "#11334b",
              fontWeight: 600,
            }}
          >
            Ładowanie użytkownika...
          </Typography>
        </Box>
      </main>
    );
  }

  if (!cert) {
    return (
      <main>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            textAlign: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#11334b",
                fontSize: "1.4rem",
                fontWeight: 700,
                mb: 1,
              }}
            >
              Nie znaleziono certyfikatu
            </Typography>

            <Typography
              sx={{
                color: "#607d8b",
                fontSize: "0.95rem",
              }}
            >
              Certyfikat o numerze {certificateId} nie jest dostępny.
            </Typography>
          </Box>
        </Box>
      </main>
    );
  }

  return (
    <main>
      <Box
        sx={{
          minHeight: "100vh",
          background: "transparent",
          pt: { xs: 4, sm: 6, md: 8 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              maxWidth: 850,
              mx: "auto",
              mb: { xs: 4, md: 6 },
              px: { xs: 2.5, sm: 4, md: 5 },
              py: { xs: 3, sm: 4 },
              textAlign: "center",
              background: "rgba(255,255,255,0.70)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              border: "1px solid rgba(255,255,255,0.85)",
              borderRadius: { xs: 3, md: 5 },
              boxShadow: "0 15px 45px rgba(17,51,75,0.10)",
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: "#11334b",
                fontSize: {
                  xs: "2rem",
                  sm: "2.5rem",
                  md: "3.2rem",
                },
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.035em",
                mb: 1.5,
              }}
            >
              Certyfikat
            </Typography>

            <Typography
              sx={{
                color: "#496575",
                fontSize: {
                  xs: "1.05rem",
                  sm: "1.2rem",
                  md: "1.4rem",
                },
                fontWeight: 600,
                lineHeight: 1.4,
                mb: 3,
              }}
            >
              {cert.badgeName}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2.5 }}
              justifyContent="center"
              alignItems="center"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.7,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: 2,
                  background: "rgba(240,249,251,0.8)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#607d8b",
                    fontWeight: 700,
                  }}
                >
                  NR
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#11334b",
                    fontWeight: 700,
                  }}
                >
                  {certificateNumber}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.7,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: 2,
                  background: "rgba(240,249,251,0.8)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#607d8b",
                    fontWeight: 700,
                  }}
                >
                  WYDANO
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#11334b",
                    fontWeight: 700,
                  }}
                >
                  {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.7,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: 2,
                  background: "rgba(240,249,251,0.8)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#607d8b",
                    fontWeight: 700,
                  }}
                >
                  WAŻNY DO
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#11334b",
                    fontWeight: 700,
                  }}
                >
                  {dayjs(cert.exam_expiry_date).format("DD.MM.YYYY")}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              mb: { xs: 4, md: 5 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 900,
                minWidth: 0,
                boxSizing: "border-box",
                p: { xs: 0.8, sm: 1.2, md: 1.8 },
                background: "rgba(255,255,255,0.42)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.8)",
                borderRadius: { xs: 2.5, md: 4 },
                boxShadow: "0 20px 60px rgba(17,51,75,0.18)",
                transition: "all 0.35s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 28px 75px rgba(17,51,75,0.24)",
                },
              }}
            >
              <Box
                id="divToPrint"
                ref={certificateRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "552 / 392",
                  backgroundImage: `url(${cert.certificate_url || cert_img})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  overflow: "hidden",
                }}
              >
                {qrValue && (
                  <QRCode
                    value={qrValue}
                    id="qrCode"
                    ref={qrCodeRef}
                    style={{
                      position: "absolute",
                      width: "7.25%",
                      height: "auto",
                      top: "47.9%",
                      left: "12%",
                    }}
                  />
                )}

                <Typography
                  sx={{
                    position: "absolute",
                    top: "44%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "80%",
                    textAlign: "center",
                    fontSize: scaledFont(25),
                    fontWeight: 600,
                    color: "#111",
                    whiteSpace: "nowrap",
                    lineHeight: 1.15,
                  }}
                >
                  {fullName}
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    top: "58%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "70%",
                    textAlign: "center",
                    fontSize: scaledFont(17),
                    fontWeight: 600,
                    color: "#111",
                    whiteSpace: "nowrap",
                    lineHeight: 1.15,
                  }}
                >
                  {cert.badgeName}
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    top: "62%",
                    left: "15.5%",
                    transform: "translate(-50%, -50%)",
                    fontSize: scaledFont(8),
                    fontWeight: 500,
                    color: "#111",
                    whiteSpace: "nowrap",
                  }}
                >
                  {certificateNumber}
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    top: "86%",
                    left: "52.6%",
                    transform: "translate(-50%, -50%)",
                    fontSize: scaledFont(8),
                    fontWeight: 500,
                    color: "#111",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              maxWidth: 700,
              mx: "auto",
              mb: 4,
              px: { xs: 2, sm: 3 },
              py: 2,
              background: "rgba(255,255,255,0.55)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.75)",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.8rem", md: "0.9rem" },
                color: "#607d8b",
                lineHeight: 1.6,
              }}
            >
              Data przyznania mikropoświadczenia:{" "}
              <Box
                component="span"
                sx={{
                  color: "#11334b",
                  fontWeight: 700,
                }}
              >
                {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
              </Box>
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<DownloadIcon />}
              onClick={printDocument}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 240 },
                px: 4,
                py: 1.4,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, #11334b 0%, #285b78 100%)",
                boxShadow: "0 8px 25px rgba(17,51,75,0.25)",
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 700,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 14px 32px rgba(17,51,75,0.35)",
                  background:
                    "linear-gradient(135deg, #11334b 0%, #285b78 100%)",
                },
              }}
            >
              Pobierz certyfikat
            </Button>
          </Stack>
        </Container>
      </Box>
    </main>
  );
}









// import React, { useEffect, useRef } from "react";
// import Button from "@mui/material/Button";
// import Stack from "@mui/material/Stack";
// import Box from "@mui/material/Box";
// import Typography from "@mui/material/Typography";
// import Container from "@mui/material/Container";
// import { useDispatch, useSelector } from "react-redux";
// import { getCertificates } from "../../Slices/certificates";
// import { useParams } from "react-router-dom";
// import DownloadIcon from "@mui/icons-material/Download";
// import jsPDF from "jspdf";
// import cert_img from "../../assets/wzor_cert.png";
// import { fontCalibri } from "../../assets/fonts/Calibri-normal.js";
// import QRCode from "react-qr-code";
// import dayjs from "dayjs";
// import html2canvas from "html2canvas";


// export default function SingleCertificate() {
//   const { certificateId } = useParams();
//   const dispatch = useDispatch();

//   const user = useSelector((state) => state.auth?.user);
//   const certificatesState = useSelector((state) => state.certificates);

//   const qrCodeRef = useRef(null);

//   const certificates = Array.isArray(certificatesState)
//     ? certificatesState
//     : Array.isArray(certificatesState?.certificates)
//       ? certificatesState.certificates
//       : Array.isArray(certificatesState?.data)
//         ? certificatesState.data
//         : [];

//   const cert = certificates.find(
//     (certificate) =>
//       String(certificate?.id) === String(certificateId)
//   );

//   useEffect(() => {

//     dispatch(getCertificates());
//   }, [dispatch]);

//   const certificateNumber = cert
//     ? `SC-03-526-01/${String(cert.id).padStart(6, "0")}`
//     : "";

//   const firstName = cert?.user?.firstname || "";
//   const lastName = cert?.user?.lastname || "";
//   const fullName = `${firstName} ${lastName}`.trim();

//   const qrValue = cert?.slug
//     ? `${process.env.REACT_APP_MEDIA_URL}/public-badge/${cert.slug}`
//     : "";



//   const printDocument = () => {
//     const input = document.getElementById("divToPrint");
//     const qrImage = document.getElementById("qrCode");

//     const scale = 2;
//     const pdfWidth = 297;

//     html2canvas(input, { useCORS: true, scale: scale }).then((canvas) => {
//       const doc = new jsPDF("l", "mm");

//       const externalImage = new Image();
//       externalImage.src =
//         "https://vcc-strapi-bucket.s3.eu-north-1.amazonaws.com/VCC_FUNDACJA_certyfikat_EUROPASS_2019_GREY_clear_faab51fa4b.png";
//       doc.addImage(
//         externalImage,
//         "JPEG",
//         0,
//         0,
//         doc.internal.pageSize.getWidth(),
//         doc.internal.pageSize.getHeight(),
//         null,
//         "NONE"
//       );

//       const centerX = doc.internal.pageSize.getWidth() / 2;

//       doc.addFileToVFS("Calibri.ttf", fontCalibri);
//       doc.addFont("Calibri.ttf", "Calibri", "normal");
//       doc.setFont("Calibri");

//       doc.setFontSize(20);
//       doc.setTextColor(0, 0, 0);

//       doc.text(dayjs(cert.issuanceDate).format("DD.MM.YYYY"), centerX, 183, {
//         align: "center",
//       });

//       doc.text(cert.badgeName, centerX, 113, {
//         align: "center",
//       });

//       doc.text(
//         dayjs(cert.exam_expiry_date).format("DD.MM.YYYY"),
//         centerX,
//         196,
//         {
//           align: "center",
//         }
//       );

//       doc.text(`${cert.user.firstname} ${cert.user.lastname}`, centerX, 90, {
//         align: "center",
//       });

//       doc.setFontSize(8);
//       doc.text(`SC-03-526-01-${cert.id}`, 54, 131, { align: "center" });

//       doc.addSvgAsImage(qrCodeRef.current.outerHTML, 31, 92, 33, 33);
//       setTimeout(() => doc.save("certificate.pdf"), 500);
//     });
//   };

//   if (!user?.email) {
//     return (
//       <main>
//         <Box
//           sx={{
//             minHeight: "100vh",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             px: 3,
//           }}
//         >
//           <Typography
//             sx={{
//               color: "#11334b",
//               fontWeight: 600,
//             }}
//           >
//             Ładowanie użytkownika...
//           </Typography>
//         </Box>
//       </main>
//     );
//   }

//   if (!cert) {
//     return (
//       <main>
//         <Box
//           sx={{
//             minHeight: "100vh",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             px: 3,
//             textAlign: "center",
//           }}
//         >
//           <Box>
//             <Typography
//               sx={{
//                 color: "#11334b",
//                 fontSize: "1.4rem",
//                 fontWeight: 700,
//                 mb: 1,
//               }}
//             >
//               Nie znaleziono certyfikatu
//             </Typography>

//             <Typography
//               sx={{
//                 color: "#607d8b",
//                 fontSize: "0.95rem",
//               }}
//             >
//               Certyfikat o numerze {certificateId} nie jest dostępny.
//             </Typography>
//           </Box>
//         </Box>
//       </main>
//     );
//   }

//   return (
//     <main>
//       <Box
//         sx={{
//           minHeight: "100vh",
//           background: "transparent",
//           pt: { xs: 4, sm: 6, md: 8 },
//           pb: { xs: 6, md: 10 },
//         }}
//       >
//         <Container maxWidth="lg">
//           <Box
//             sx={{
//               maxWidth: 850,
//               mx: "auto",
//               mb: { xs: 4, md: 6 },
//               px: { xs: 2.5, sm: 4, md: 5 },
//               py: { xs: 3, sm: 4 },
//               textAlign: "center",
//               background: "rgba(255,255,255,0.70)",
//               backdropFilter: "blur(14px)",
//               WebkitBackdropFilter: "blur(14px)",
//               border: "1px solid rgba(255,255,255,0.85)",
//               borderRadius: { xs: 3, md: 5 },
//               boxShadow: "0 15px 45px rgba(17,51,75,0.10)",
//             }}
//           >
//             <Typography
//               component="h1"
//               sx={{
//                 color: "#11334b",
//                 fontSize: {
//                   xs: "2rem",
//                   sm: "2.5rem",
//                   md: "3.2rem",
//                 },
//                 fontWeight: 800,
//                 lineHeight: 1.1,
//                 letterSpacing: "-0.035em",
//                 mb: 1.5,
//               }}
//             >
//               Certyfikat
//             </Typography>

//             <Typography
//               sx={{
//                 color: "#496575",
//                 fontSize: {
//                   xs: "1.05rem",
//                   sm: "1.2rem",
//                   md: "1.4rem",
//                 },
//                 fontWeight: 600,
//                 lineHeight: 1.4,
//                 mb: 3,
//               }}
//             >
//               {cert.badgeName}
//             </Typography>

//             <Stack
//               direction={{ xs: "column", sm: "row" }}
//               spacing={{ xs: 1, sm: 2.5 }}
//               justifyContent="center"
//               alignItems="center"
//             >
//               <Box
//                 sx={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 0.7,
//                   px: 1.5,
//                   py: 0.7,
//                   borderRadius: 2,
//                   background: "rgba(240,249,251,0.8)",
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     fontSize: "0.75rem",
//                     color: "#607d8b",
//                     fontWeight: 700,
//                   }}
//                 >
//                   NR
//                 </Typography>

//                 <Typography
//                   sx={{
//                     fontSize: "0.8rem",
//                     color: "#11334b",
//                     fontWeight: 700,
//                   }}
//                 >
//                   {certificateNumber}
//                 </Typography>
//               </Box>

//               <Box
//                 sx={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 0.7,
//                   px: 1.5,
//                   py: 0.7,
//                   borderRadius: 2,
//                   background: "rgba(240,249,251,0.8)",
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     fontSize: "0.75rem",
//                     color: "#607d8b",
//                     fontWeight: 700,
//                   }}
//                 >
//                   WYDANO
//                 </Typography>

//                 <Typography
//                   sx={{
//                     fontSize: "0.8rem",
//                     color: "#11334b",
//                     fontWeight: 700,
//                   }}
//                 >
//                   {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
//                 </Typography>
//               </Box>

//               <Box
//                 sx={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 0.7,
//                   px: 1.5,
//                   py: 0.7,
//                   borderRadius: 2,
//                   background: "rgba(240,249,251,0.8)",
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     fontSize: "0.75rem",
//                     color: "#607d8b",
//                     fontWeight: 700,
//                   }}
//                 >
//                   WAŻNY DO
//                 </Typography>

//                 <Typography
//                   sx={{
//                     fontSize: "0.8rem",
//                     color: "#11334b",
//                     fontWeight: 700,
//                   }}
//                 >
//                   {dayjs(cert.exam_expiry_date).format("DD.MM.YYYY")}
//                 </Typography>
//               </Box>
//             </Stack>
//           </Box>

//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "center",
//               width: "100%",
//               mb: { xs: 4, md: 5 },
//             }}
//           >
//             <Box
//               sx={{
//                 position: "relative",
//                 width: "100%",
//                 maxWidth: 900,
//                 p: { xs: 0.8, sm: 1.2, md: 1.8 },
//                 background: "rgba(255,255,255,0.42)",
//                 backdropFilter: "blur(8px)",
//                 WebkitBackdropFilter: "blur(8px)",
//                 border: "1px solid rgba(255,255,255,0.8)",
//                 borderRadius: { xs: 2.5, md: 4 },
//                 boxShadow: "0 20px 60px rgba(17,51,75,0.18)",
//                 transition: "all 0.35s ease",
//                 "&:hover": {
//                   transform: "translateY(-4px)",
//                   boxShadow: "0 28px 75px rgba(17,51,75,0.24)",
//                 },
//               }}
//             >
//               <Box
//                 id="divToPrint"
//                 sx={{
//                   position: "relative",
//                   width: "100%",
//                   aspectRatio: "552 / 392",
//                   backgroundImage: `url(${cert.certificate_url || cert_img})`,
//                   backgroundSize: "cover",
//                   backgroundPosition: "center",
//                   backgroundRepeat: "no-repeat",
//                   overflow: "hidden",
//                 }}
//               >
//                 {qrValue && (
//                   <QRCode
//                     value={qrValue}
//                     id="qrCode"
//                     ref={qrCodeRef}
//                     style={{
//                       position: "absolute",
//                       width: "7.25%",
//                       height: "auto",
//                       top: "47.9%",
//                       left: "12%",
//                     }}
//                   />
//                 )}

//                 <Typography
//                   sx={{
//                     position: "absolute",
//                     top: "44%",
//                     left: "50%",
//                     transform: "translate(-50%, -50%)",
//                     width: "80%",
//                     textAlign: "center",
//                     fontSize: "clamp(14px, 2.3vw, 25px)",
//                     fontWeight: 600,
//                     color: "#111",
//                     whiteSpace: "nowrap",
//                     lineHeight: 1.15,
//                   }}
//                 >
//                   {fullName}
//                 </Typography>

//                 <Typography
//                   sx={{
//                     position: "absolute",
//                     top: "58%",
//                     left: "50%",
//                     transform: "translate(-50%, -50%)",
//                     width: "70%",
//                     textAlign: "center",
//                     fontSize: "clamp(10px, 1.5vw, 17px)",
//                     fontWeight: 600,
//                     color: "#111",
//                     whiteSpace: "nowrap",
//                     lineHeight: 1.15,
//                   }}
//                 >
//                   {cert.badgeName}
//                 </Typography>

//                 <Typography
//                   sx={{
//                     position: "absolute",
//                     top: "62%",
//                     left: "15.5%",
//                     transform: "translate(-50%, -50%)",
//                     fontSize: "clamp(5px, 0.65vw, 8px)",
//                     fontWeight: 500,
//                     color: "#111",
//                     whiteSpace: "nowrap",
//                   }}
//                 >
//                   {certificateNumber}
//                 </Typography>

//                 <Typography
//                   sx={{
//                     position: "absolute",
//                     top: "86%",
//                     left: "52.6%",
//                     transform: "translate(-50%, -50%)",
//                     fontSize: "clamp(5px, 0.7vw, 8px)",
//                     fontWeight: 500,
//                     color: "#111",
//                     whiteSpace: "nowrap",
//                   }}
//                 >
//                   {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
//                 </Typography>
//               </Box>
//             </Box>
//           </Box>

//           <Box
//             sx={{
//               maxWidth: 700,
//               mx: "auto",
//               mb: 4,
//               px: { xs: 2, sm: 3 },
//               py: 2,
//               background: "rgba(255,255,255,0.55)",
//               backdropFilter: "blur(10px)",
//               WebkitBackdropFilter: "blur(10px)",
//               borderRadius: 3,
//               border: "1px solid rgba(255,255,255,0.75)",
//               textAlign: "center",
//             }}
//           >
//             <Typography
//               sx={{
//                 fontSize: { xs: "0.8rem", md: "0.9rem" },
//                 color: "#607d8b",
//                 lineHeight: 1.6,
//               }}
//             >
//               Data przyznania mikropoświadczenia:{" "}
//               <Box
//                 component="span"
//                 sx={{
//                   color: "#11334b",
//                   fontWeight: 700,
//                 }}
//               >
//                 {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
//               </Box>
//             </Typography>
//           </Box>

//           <Stack
//             direction={{ xs: "column", sm: "row" }}
//             spacing={2}
//             justifyContent="center"
//             alignItems="center"
//           >
//             <Button
//               variant="contained"
//               size="large"
//               endIcon={<DownloadIcon />}
//               onClick={printDocument}
//               sx={{
//                 width: { xs: "100%", sm: "auto" },
//                 minWidth: { sm: 240 },
//                 px: 4,
//                 py: 1.4,
//                 borderRadius: 3,
//                 background:
//                   "linear-gradient(135deg, #11334b 0%, #285b78 100%)",
//                 boxShadow: "0 8px 25px rgba(17,51,75,0.25)",
//                 textTransform: "none",
//                 fontSize: "0.95rem",
//                 fontWeight: 700,
//                 transition: "all 0.3s ease",
//                 "&:hover": {
//                   transform: "translateY(-3px)",
//                   boxShadow: "0 14px 32px rgba(17,51,75,0.35)",
//                   background:
//                     "linear-gradient(135deg, #11334b 0%, #285b78 100%)",
//                 },
//               }}
//             >
//               Pobierz certyfikat
//             </Button>
//           </Stack>
//         </Container>
//       </Box>
//     </main>
//   );
// }








// // import React, { useState, useEffect, useRef } from "react";
// // import Button from "@mui/material/Button";
// // import Stack from "@mui/material/Stack";
// // import Box from "@mui/material/Box";
// // import Typography from "@mui/material/Typography";
// // import Container from "@mui/material/Container";
// // import { useDispatch, useSelector } from "react-redux";
// // import { getCertificates } from "../../Slices/certificates";
// // import { useParams } from "react-router-dom";
// // import { createSelector } from "reselect";
// // import Copyright from "../../Components/Copyright";
// // import DownloadIcon from "@mui/icons-material/Download";
// // import PrintIcon from "@mui/icons-material/Print";
// // import { useReactToPrint } from "react-to-print";
// // import useDownload from "../../Hooks/useDownload";
// // import { styled } from "@mui/material";
// // import jsPDF from "jspdf";
// // import html2canvas from "html2canvas";
// // import cert_img from "../../assets/wzor_cert.png";
// // import { fontCalibri } from "../../assets/fonts/Calibri-normal.js";
// // import QRCode from "react-qr-code";
// // import dayjs from "dayjs";

// // const useStyles = () => ({
// //   cert: {
// //     position: "relative",
// //     imageRendering: "auto",
// //   },
// //   userName: {
// //     position: "absolute",
// //     top: "42%",
// //     left: "50%",
// //     transform: "translate(-50%, -50%)",
// //   },
// //   CertNumber: {
// //     fontSize: "6px",
// //     position: "absolute",
// //     top: "62%",
// //     left: "17%",
// //     transform: "translate(-50%, -50%)",
// //   },
// //   IssuanceDate: {
// //     fontSize: "6px",
// //     position: "absolute",
// //     top: "86%",
// //     left: "50%",
// //     transform: "translate(-50%, -50%)",
// //   },
// //   CertName: {
// //     position: "absolute",
// //     top: "55%",
// //     left: "50%",
// //     transform: "translate(-50%, -50%)",
// //   },
// // });

// // const Cert = styled("div")(({ theme }) => ({
// //   ...useStyles(theme).cert,
// // }));

// // const UserName = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).userName,
// // }));

// // const CertNumber = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).CertNumber,
// // }));
// // const IssuanceDate = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).IssuanceDate,
// // }));
// // const CertName = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).CertName,
// // }));

// // export default function SingleCertificate() {
// //   let { certificateId } = useParams();
// //   const dispatch = useDispatch();
// //   const { user } = useSelector((state) => state.auth);
// //   const classes = useStyles();
// //   const qrCodeRef = useRef(null);
// //   // const [qrcc, setQrcc] = useState();

// //   const { download } = useDownload();

// //   // const selectCertificates = createSelector(
// //   //   (state) => state.certificates,
// //   //   (certificates) =>
// //   //     certificates.filter((cert) => cert.attributes.cerId === certificateId)[0]
// //   //       .attributes
// //   // );

// //   const selectCertificates = createSelector(
// //     (state) => state.certificates,
// //     (certificates) =>
// //       certificates.filter(
// //         (certificate) => certificate.id === Number(certificateId)
// //       )[0]
// //   );

// //   const cert = useSelector(selectCertificates);

// //   const componentRef = useRef();
// //   const handlePrint = useReactToPrint({
// //     content: () => componentRef.current,
// //   });

// //   useEffect(() => {
// //     dispatch(getCertificates(user.email));
// //   }, [user, dispatch]);

// //   const printDocument = () => {
// //     const input = document.getElementById("divToPrint");
// //     const qrImage = document.getElementById("qrCode");

// //     const scale = 2;
// //     const pdfWidth = 297;

// //     html2canvas(input, { useCORS: true, scale: scale }).then((canvas) => {
// //       const doc = new jsPDF("l", "mm");

// //       const externalImage = new Image();
// //       externalImage.src =
// //         "http://localhost:3000/static/media/wzor_cert.2593f7a02c32f220c55d.png";
// //       doc.addImage(
// //         externalImage,
// //         "JPEG",
// //         0,
// //         0,
// //         doc.internal.pageSize.getWidth(),
// //         doc.internal.pageSize.getHeight(),
// //         null,
// //         "NONE"
// //       );

// //       const centerX = doc.internal.pageSize.getWidth() / 2;

// //       doc.addFileToVFS("Calibri.ttf", fontCalibri);
// //       doc.addFont("Calibri.ttf", "Calibri", "normal");
// //       doc.setFont("Calibri");

// //       doc.setFontSize(20);
// //       doc.setTextColor(0, 0, 0);

// //       doc.text(dayjs(cert.issuanceDate).format("DD.MM.YYYY"), centerX, 183, {
// //         align: "center",
// //       });

// //       doc.text(cert.badgeName, centerX, 113, {
// //         align: "center",
// //       });

// //       doc.text(
// //         dayjs(cert.exam_expiry_date).format("DD.MM.YYYY"),
// //         centerX,
// //         196,
// //         {
// //           align: "center",
// //         }
// //       );

// //       doc.text(`${cert.user.firstname} ${cert.user.lastname}`, centerX, 90, {
// //         align: "center",
// //       });

// //       doc.setFontSize(8);
// //       doc.text(`SC-03-526-01-${cert.id}`, 54, 131, { align: "center" });

// //       doc.addSvgAsImage(qrCodeRef.current.outerHTML, 31, 92, 33, 33);
// //       setTimeout(() => doc.save("certificate.pdf"), 500);
// //     });
// //   };


// //   return (
// //     <main>
// //       {cert && (
// //         <Box
// //           sx={{
// //             minHeight: "100vh",
// //             background: "transparent",
// //             pt: { xs: 4, sm: 6, md: 8 },
// //             pb: { xs: 6, md: 10 },
// //           }}
// //         >
// //           <Container maxWidth="lg">

// //             <Box
// //               sx={{
// //                 maxWidth: 850,
// //                 mx: "auto",
// //                 mb: { xs: 4, md: 6 },
// //                 px: { xs: 2.5, sm: 4, md: 5 },
// //                 py: { xs: 3, sm: 4 },

// //                 textAlign: "center",

// //                 background: "rgba(255,255,255,0.70)",
// //                 backdropFilter: "blur(14px)",
// //                 WebkitBackdropFilter: "blur(14px)",

// //                 border: "1px solid rgba(255,255,255,0.85)",
// //                 borderRadius: { xs: 3, md: 5 },

// //                 boxShadow: "0 15px 45px rgba(17,51,75,0.10)",
// //               }}
// //             >
// //               <Typography
// //                 component="h1"
// //                 sx={{
// //                   color: "#11334b",
// //                   fontSize: {
// //                     xs: "2rem",
// //                     sm: "2.5rem",
// //                     md: "3.2rem",
// //                   },
// //                   fontWeight: 800,
// //                   lineHeight: 1.1,
// //                   letterSpacing: "-0.035em",
// //                   mb: 1.5,
// //                 }}
// //               >
// //                 Certyfikat
// //               </Typography>

// //               <Typography
// //                 sx={{
// //                   color: "#496575",
// //                   fontSize: {
// //                     xs: "1.05rem",
// //                     sm: "1.2rem",
// //                     md: "1.4rem",
// //                   },
// //                   fontWeight: 600,
// //                   lineHeight: 1.4,
// //                   mb: 3,
// //                 }}
// //               >
// //                 {cert.badgeName}
// //               </Typography>

// //               <Stack
// //                 direction={{ xs: "column", sm: "row" }}
// //                 spacing={{ xs: 1, sm: 2.5 }}
// //                 justifyContent="center"
// //                 alignItems="center"
// //               >
// //                 {/* NUMER */}
// //                 <Box
// //                   sx={{
// //                     display: "flex",
// //                     alignItems: "center",
// //                     gap: 0.7,
// //                     px: 1.5,
// //                     py: 0.7,
// //                     borderRadius: 2,
// //                     background: "rgba(240,249,251,0.8)",
// //                   }}
// //                 >
// //                   <Typography
// //                     sx={{
// //                       fontSize: "0.75rem",
// //                       color: "#607d8b",
// //                       fontWeight: 700,
// //                     }}
// //                   >
// //                     NR
// //                   </Typography>

// //                   <Typography
// //                     sx={{
// //                       fontSize: "0.8rem",
// //                       color: "#11334b",
// //                       fontWeight: 700,
// //                     }}
// //                   >
// //                     {cert.id}
// //                   </Typography>
// //                 </Box>

// //                 <Box
// //                   sx={{
// //                     display: "flex",
// //                     alignItems: "center",
// //                     gap: 0.7,
// //                     px: 1.5,
// //                     py: 0.7,
// //                     borderRadius: 2,
// //                     background: "rgba(240,249,251,0.8)",
// //                   }}
// //                 >
// //                   <Typography
// //                     sx={{
// //                       fontSize: "0.75rem",
// //                       color: "#607d8b",
// //                       fontWeight: 700,
// //                     }}
// //                   >
// //                     WYDANO
// //                   </Typography>

// //                   <Typography
// //                     sx={{
// //                       fontSize: "0.8rem",
// //                       color: "#11334b",
// //                       fontWeight: 700,
// //                     }}
// //                   >
// //                     {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
// //                   </Typography>
// //                 </Box>

// //                 <Box
// //                   sx={{
// //                     display: "flex",
// //                     alignItems: "center",
// //                     gap: 0.7,
// //                     px: 1.5,
// //                     py: 0.7,
// //                     borderRadius: 2,
// //                     background: "rgba(240,249,251,0.8)",
// //                   }}
// //                 >
// //                   <Typography
// //                     sx={{
// //                       fontSize: "0.75rem",
// //                       color: "#607d8b",
// //                       fontWeight: 700,
// //                     }}
// //                   >
// //                     WAŻNY DO
// //                   </Typography>

// //                   <Typography
// //                     sx={{
// //                       fontSize: "0.8rem",
// //                       color: "#11334b",
// //                       fontWeight: 700,
// //                     }}
// //                   >
// //                     {dayjs(cert.exam_expiry_date).format("DD.MM.YYYY")}
// //                   </Typography>
// //                 </Box>
// //               </Stack>
// //             </Box>

// //             <Box
// //               sx={{
// //                 display: "flex",
// //                 justifyContent: "center",
// //                 width: "100%",
// //                 mb: { xs: 4, md: 5 },
// //               }}
// //             >
// //               <Box
// //                 sx={{
// //                   position: "relative",
// //                   width: "100%",
// //                   maxWidth: "900px",

// //                   p: { xs: 0.8, sm: 1.2, md: 1.8 },

// //                   background: "rgba(255,255,255,0.42)",
// //                   backdropFilter: "blur(8px)",
// //                   WebkitBackdropFilter: "blur(8px)",

// //                   border: "1px solid rgba(255,255,255,0.8)",
// //                   borderRadius: { xs: 2.5, md: 4 },

// //                   boxShadow:
// //                     "0 20px 60px rgba(17,51,75,0.18)",

// //                   transition: "all 0.35s ease",

// //                   "&:hover": {
// //                     transform: "translateY(-4px)",
// //                     boxShadow:
// //                       "0 28px 75px rgba(17,51,75,0.24)",
// //                   },
// //                 }}
// //               >
// //                 <Box
// //                   id="divToPrint"
// //                   sx={{
// //                     position: "relative",
// //                     width: "100%",
// //                     aspectRatio: "552 / 392",

// //                     backgroundImage: `url(${cert.certificate_url})`,
// //                     backgroundSize: "cover",
// //                     backgroundPosition: "center",
// //                     backgroundRepeat: "no-repeat",

// //                     overflow: "hidden",
// //                   }}
// //                 >
// //                   <QRCode
// //                     value={`${process.env.REACT_APP_MEDIA_URL} /public-badge/${cert.slug} `}
// //                     id="qrCode"
// //                     ref={qrCodeRef}
// //                     style={{
// //                       position: "absolute",
// //                       width: "7.25%",
// //                       height: "auto",
// //                       top: "47.9%",
// //                       left: "12%",
// //                     }}
// //                   />
// //                   <UserName
// //                     sx={{
// //                       position: "absolute",
// //                       top: "42%",
// //                       left: "50%",
// //                       transform: "translate(-50%, -50%)",

// //                       width: "80%",
// //                       textAlign: "center",

// //                       fontSize: "clamp(14px, 2.3vw, 25px)",
// //                       fontWeight: 600,
// //                       color: "#111",

// //                       whiteSpace: "nowrap",
// //                     }}
// //                   >
// //                     {`${cert.user.firstname} ${cert.user.lastname} `}
// //                   </UserName>

// //                   <CertName
// //                     sx={{
// //                       position: "absolute",
// //                       top: "55%",
// //                       left: "50%",
// //                       transform: "translate(-50%, -50%)",

// //                       width: "70%",
// //                       textAlign: "center",

// //                       fontSize: "clamp(10px, 1.5vw, 17px)",
// //                       fontWeight: 600,
// //                       color: "#111",

// //                       whiteSpace: "nowrap",
// //                     }}
// //                   >
// //                     {cert.badgeName}
// //                   </CertName>

// //                   <CertNumber
// //                     sx={{
// //                       position: "absolute",
// //                       top: "62%",
// //                       left: "17%",
// //                       transform: "translate(-50%, -50%)",

// //                       fontSize: "clamp(5px, 0.65vw, 8px)",
// //                       fontWeight: 500,
// //                       color: "#111",

// //                       whiteSpace: "nowrap",
// //                     }}
// //                   >
// //                     {`SC-03 - 526-01 - ${cert.id} `}
// //                   </CertNumber>

// //                   <IssuanceDate
// //                     sx={{
// //                       position: "absolute",
// //                       top: "86%",
// //                       left: "50%",
// //                       transform: "translate(-50%, -50%)",

// //                       fontSize: "clamp(5px, 0.7vw, 8px)",
// //                       fontWeight: 500,
// //                       color: "#111",

// //                       whiteSpace: "nowrap",
// //                     }}
// //                   >
// //                     {dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
// //                   </IssuanceDate>
// //                 </Box>
// //               </Box>
// //             </Box>
// //             <Box
// //               sx={{
// //                 maxWidth: 700,
// //                 mx: "auto",
// //                 mb: 4,
// //                 px: { xs: 2, sm: 3 },
// //                 py: 2,

// //                 background: "rgba(255,255,255,0.55)",
// //                 backdropFilter: "blur(10px)",
// //                 WebkitBackdropFilter: "blur(10px)",

// //                 borderRadius: 3,
// //                 border: "1px solid rgba(255,255,255,0.75)",

// //                 textAlign: "center",
// //               }}
// //             >
// //               <Typography
// //                 sx={{
// //                   fontSize: { xs: "0.8rem", md: "0.9rem" },
// //                   color: "#607d8b",
// //                   lineHeight: 1.6,
// //                 }}
// //               >
// //                 Data przyznania mikropoświadczenia:{" "}
// //                 <Box
// //                   component="span"
// //                   sx={{
// //                     color: "#11334b",
// //                     fontWeight: 700,
// //                   }}
// //                 >
// //                   {dayjs(cert.exam_expiry_date).format("DD.MM.YYYY")}
// //                 </Box>
// //               </Typography>
// //             </Box>

// //             <Stack
// //               direction={{ xs: "column", sm: "row" }}
// //               spacing={2}
// //               justifyContent="center"
// //               alignItems="center"
// //             >
// //               <Button
// //                 variant="contained"
// //                 size="large"
// //                 endIcon={<DownloadIcon />}
// //                 onClick={printDocument}
// //                 sx={{
// //                   width: { xs: "100%", sm: "auto" },
// //                   minWidth: { sm: 240 },

// //                   px: 4,
// //                   py: 1.4,

// //                   borderRadius: 3,

// //                   background:
// //                     "linear-gradient(135deg, #11334b 0%, #285b78 100%)",

// //                   boxShadow:
// //                     "0 8px 25px rgba(17,51,75,0.25)",

// //                   textTransform: "none",
// //                   fontSize: "0.95rem",
// //                   fontWeight: 700,

// //                   transition: "all 0.3s ease",

// //                   "&:hover": {
// //                     transform: "translateY(-3px)",
// //                     boxShadow:
// //                       "0 14px 32px rgba(17,51,75,0.35)",
// //                     background:
// //                       "linear-gradient(135deg, #11334b 0%, #285b78 100%)",
// //                   },
// //                 }}
// //               >
// //                 Pobierz certyfikat
// //               </Button>
// //             </Stack>

// //           </Container>
// //         </Box>
// //       )}
// //     </main>
// //   );
// // }




// // // import React, { useState, useEffect, useRef } from "react";
// // // import Button from "@mui/material/Button";
// // // import Stack from "@mui/material/Stack";
// // // import Box from "@mui/material/Box";
// // // import Typography from "@mui/material/Typography";
// // // import Container from "@mui/material/Container";
// // // import { useDispatch, useSelector } from "react-redux";
// // // import { getCertificates } from "../../Slices/certificates";
// // // import { useParams } from "react-router-dom";
// // // import { createSelector } from "reselect";
// // // import Copyright from "../../Components/Copyright";
// // // import DownloadIcon from "@mui/icons-material/Download";
// // // import PrintIcon from "@mui/icons-material/Print";
// // // import { useReactToPrint } from "react-to-print";
// // // import useDownload from "../../Hooks/useDownload";

// // // export default function SingleCertificate() {
// // //   let { certificateId } = useParams();
// // //   const dispatch = useDispatch();
// // //   const { user } = useSelector((state) => state.auth);

// // //   const { download } = useDownload();

// // //   const selectCertificates = createSelector(
// // //     (state) => state.certificates,
// // //     (certificates) =>
// // //       certificates.filter((cert) => cert.attributes.cerId === certificateId)[0]
// // //         .attributes
// // //   );

// // //   const componentRef = useRef();
// // //   const handlePrint = useReactToPrint({
// // //     content: () => componentRef.current,
// // //   });

// // //   const cert = useSelector(selectCertificates);

// // //   useEffect(() => {
// // //     dispatch(getCertificates(user.email));
// // //   }, [user]);

// // //   return (
// // //     <main>
// // //       {cert && (
// // //         <Box
// // //           sx={{
// // //             bgcolor: "background.paper",
// // //             pt: 8,
// // //             pb: 6,
// // //           }}
// // //         >
// // //           <Container maxWidth="sm">
// // //             <Typography
// // //               component="h1"
// // //               variant="h2"
// // //               align="center"
// // //               color="text.primary"
// // //               gutterBottom
// // //             >
// // //               Certyfikat
// // //             </Typography>
// // //             <Typography
// // //               variant="h4"
// // //               align="center"
// // //               color="text.secondary"
// // //               paragraph
// // //             >
// // //               {cert && cert.name}
// // //             </Typography>
// // //             <Typography
// // //               variant="h6"
// // //               align="center"
// // //               color="text.secondary"
// // //               paragraph
// // //             >
// // //               Numer certyfikatu: {cert && cert.cerId}
// // //             </Typography>
// // //             <Typography
// // //               variant="h6"
// // //               align="center"
// // //               color="text.secondary"
// // //               paragraph
// // //             >
// // //               Data wydania: {cert && cert.date}
// // //             </Typography>
// // //             <div style={{ marginTop: "20px", marginBottom: "20px" }}>
// // //               <img
// // //                 src={cert && cert.url}
// // //                 alt={cert && cert.name}
// // //                 loading="lazy"
// // //                 style={{ maxWidth: "100%" }}
// // //                 ref={componentRef}
// // //               />
// // //             </div>
// // //             <Stack
// // //               sx={{ pt: 4 }}
// // //               direction="row"
// // //               spacing={2}
// // //               justifyContent="center"
// // //             >
// // //               <Button variant="outlined" endIcon={<DownloadIcon />}>
// // //                 <a
// // //                   style={{ textDecoration: "none", color: "#d2ab67" }}
// // //                   href={cert.url}
// // //                   onClick={() =>
// // //                     download(
// // //                       "https://vccsystem.eu/wp-content/uploads/2021/01/certyfikat-bez-logotypu-scaled.jpg"
// // //                     )
// // //                   }
// // //                   target="_blank"
// // //                   rel="noreferrer"
// // //                 >
// // //                   Pobierz
// // //                 </a>
// // //               </Button>
// // //               <Button
// // //                 variant="outlined"
// // //                 endIcon={<PrintIcon />}
// // //                 onClick={handlePrint}
// // //               >
// // //                 Drukuj
// // //               </Button>
// // //             </Stack>
// // //           </Container>
// // //         </Box>
// // //       )}
// // //       {/* <Copyright sx={{ mt: 8, mb: 4 }} /> */}
// // //     </main>
// // //   );
// // // }

// // import React, { useState, useEffect, useRef } from "react";
// // import Button from "@mui/material/Button";
// // import Stack from "@mui/material/Stack";
// // import Box from "@mui/material/Box";
// // import Typography from "@mui/material/Typography";
// // import Container from "@mui/material/Container";
// // import { useDispatch, useSelector } from "react-redux";
// // import { getCertificates } from "../../Slices/certificates";
// // import { useParams } from "react-router-dom";
// // import { createSelector } from "reselect";
// // import Copyright from "../../Components/Copyright";
// // import DownloadIcon from "@mui/icons-material/Download";
// // import PrintIcon from "@mui/icons-material/Print";
// // import { useReactToPrint } from "react-to-print";
// // import useDownload from "../../Hooks/useDownload";
// // import { styled } from "@mui/material";
// // import jsPDF from "jspdf";
// // import html2canvas from "html2canvas";
// // import cert_img from "../../assets/wzor_cert.png";
// // import { fontCalibri } from "../../assets/fonts/Calibri-normal.js";
// // import QRCode from "react-qr-code";
// // import dayjs from "dayjs";

// // const useStyles = () => ({
// //   cert: {
// //     position: "relative",
// //     imageRendering: "auto",
// //   },
// //   userName: {
// //     position: "absolute",
// //     top: "42%",
// //     left: "50%",
// //     transform: "translate(-50%, -50%)",
// //   },
// //   CertNumber: {
// //     fontSize: "6px",
// //     position: "absolute",
// //     top: "62%",
// //     left: "17%",
// //     transform: "translate(-50%, -50%)",
// //   },
// //   IssuanceDate: {
// //     fontSize: "6px",
// //     position: "absolute",
// //     top: "86%",
// //     left: "50%",
// //     transform: "translate(-50%, -50%)",
// //   },
// //   CertName: {
// //     position: "absolute",
// //     top: "55%",
// //     left: "50%",
// //     transform: "translate(-50%, -50%)",
// //   },
// // });

// // const Cert = styled("div")(({ theme }) => ({
// //   ...useStyles(theme).cert,
// // }));

// // const UserName = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).userName,
// // }));

// // const CertNumber = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).CertNumber,
// // }));
// // const IssuanceDate = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).IssuanceDate,
// // }));
// // const CertName = styled(Typography)(({ theme }) => ({
// //   ...useStyles(theme).CertName,
// // }));

// // export default function SingleCertificate() {
// //   let { certificateId } = useParams();
// //   const dispatch = useDispatch();
// //   const { user } = useSelector((state) => state.auth);
// //   const classes = useStyles();
// //   const qrCodeRef = useRef(null);
// //   // const [qrcc, setQrcc] = useState();

// //   const { download } = useDownload();

// //   // const selectCertificates = createSelector(
// //   //   (state) => state.certificates,
// //   //   (certificates) =>
// //   //     certificates.filter((cert) => cert.attributes.cerId === certificateId)[0]
// //   //       .attributes
// //   // );

// //   const selectCertificates = createSelector(
// //     (state) => state.certificates,
// //     (certificates) =>
// //       certificates.filter(
// //         (certificate) => certificate.id === Number(certificateId)
// //       )[0]
// //   );

// //   const cert = useSelector(selectCertificates);

// //   const componentRef = useRef();
// //   const handlePrint = useReactToPrint({
// //     content: () => componentRef.current,
// //   });

// //   useEffect(() => {
// //     dispatch(getCertificates(user.email));
// //   }, [user]);

// //   const printDocument = () => {
// //     const input = document.getElementById("divToPrint");
// //     const qrImage = document.getElementById("qrCode");

// //     const scale = 2;
// //     const pdfWidth = 297;

// //     html2canvas(input, { useCORS: true, scale: scale }).then((canvas) => {
// //       const doc = new jsPDF("l", "mm");

// //       const externalImage = new Image();
// //       externalImage.src =
// //         "http://localhost:3000/static/media/wzor_cert.2593f7a02c32f220c55d.png";
// //       doc.addImage(
// //         externalImage,
// //         "JPEG",
// //         0,
// //         0,
// //         doc.internal.pageSize.getWidth(),
// //         doc.internal.pageSize.getHeight(),
// //         null,
// //         "NONE"
// //       );

// //       const centerX = doc.internal.pageSize.getWidth() / 2;

// //       doc.addFileToVFS("Calibri.ttf", fontCalibri);
// //       doc.addFont("Calibri.ttf", "Calibri", "normal");
// //       doc.setFont("Calibri");

// //       doc.setFontSize(20);
// //       doc.setTextColor(0, 0, 0);

// //       doc.text(dayjs(cert.issuanceDate).format("DD.MM.YYYY"), centerX, 183, {
// //         align: "center",
// //       });

// //       doc.text(cert.badgeName, centerX, 113, {
// //         align: "center",
// //       });

// //       doc.text(
// //         dayjs(cert.exam_expiry_date).format("DD.MM.YYYY"),
// //         centerX,
// //         196,
// //         {
// //           align: "center",
// //         }
// //       );

// //       doc.text(`${cert.user.firstname} ${cert.user.lastname}`, centerX, 90, {
// //         align: "center",
// //       });

// //       doc.setFontSize(8);
// //       doc.text("SC-03-526-01-6827463", 54, 131, { align: "center" });

// //       doc.addSvgAsImage(qrCodeRef.current.outerHTML, 31, 92, 33, 33);
// //       setTimeout(() => doc.save("certificate.pdf"), 500);
// //     });
// //   };

// //   return (
// //     <main>
// //       {cert && (
// //         <Box
// //           sx={{
// //             bgcolor: "background.paper",
// //             pt: 8,
// //             pb: 6,
// //           }}
// //         >
// //           <Container maxWidth="sm">
// //             <Typography
// //               component="h1"
// //               variant="h2"
// //               align="center"
// //               color="text.primary"
// //               gutterBottom
// //             >
// //               Certyfikat
// //             </Typography>
// //             <Typography
// //               variant="h4"
// //               align="center"
// //               color="text.secondary"
// //               paragraph
// //             >
// //               {cert && cert.badgeName}
// //             </Typography>
// //             <Typography
// //               variant="h6"
// //               align="center"
// //               color="text.secondary"
// //               paragraph
// //             >
// //               Numer certyfikatu: {cert && cert.id}
// //             </Typography>
// //             <Typography
// //               variant="h6"
// //               align="center"
// //               color="text.secondary"
// //               paragraph
// //             >
// //               Data wydania:{" "}
// //               {cert && dayjs(cert.issuanceDate).format("DD.MM.YYYY")}
// //             </Typography>
// //             <Typography
// //               variant="h6"
// //               align="center"
// //               color="text.secondary"
// //               paragraph
// //             >
// //               Data przyznania mikropoświadczenia:{" "}
// //               {cert && dayjs(cert.exam_expiry_date).format("DD.MM.YYYY")}
// //             </Typography>
// //             <div
// //               style={{
// //                 // backgroundImage: `url(${cert_img})`,
// //                 backgroundImage: `url(${cert.certificate_url})`,
// //                 backgroundSize: "contain",
// //               }}
// //             >
// //               <Cert
// //                 id="divToPrint"
// //                 className={classes.cert}
// //                 style={{
// //                   height: "392.44px",
// //                   width: "552px",
// //                   marginTop: "20px",
// //                   marginBottom: "20px",
// //                 }}
// //               >
// //                 <QRCode
// //                   // value="http://localhost:3000/public-badge/dB7dC6C7-DF1C-dBa7-5EDB-EE6cee865cA8"
// //                   value={`${process.env.REACT_APP_MEDIA_URL}/public-badge/${cert.slug}`}
// //                   id="qrCode"
// //                   ref={qrCodeRef}
// //                   style={{
// //                     height: "auto",
// //                     maxWidth: "40px",
// //                     width: "40px",
// //                     position: "absolute",
// //                     top: "188px",
// //                     left: "66px",
// //                   }}
// //                 />
// //                 {/* <img
// //                 src={cert && cert.url}
// //                 alt={cert && cert.name}
// //                 loading="lazy"
// //                 style={{ maxWidth: "100%" }}
// //                 ref={componentRef}
// //               /> */}
// //                 <UserName
// //                   sx={{ position: "absolute" }}
// //                 >{`${cert.user.firstname} ${cert.user.lastname}`}</UserName>
// //                 <CertName
// //                   sx={{ position: "absolute" }}
// //                 >{`${cert.badgeName}`}</CertName>
// //                 <CertNumber sx={{ position: "absolute" }}>
// //                   {`SC-03-526-01-${cert.id}`}
// //                 </CertNumber>
// //                 <IssuanceDate sx={{ position: "absolute" }}>
// //                   {`${dayjs(cert.issuanceDate).format("DD.MM.YYYY")}`}
// //                 </IssuanceDate>
// //               </Cert>
// //             </div>

// //             <Stack
// //               sx={{ pt: 4 }}
// //               direction="row"
// //               spacing={2}
// //               justifyContent="center"
// //             >
// //               <Button
// //                 variant="outlined"
// //                 endIcon={<DownloadIcon />}
// //                 onClick={printDocument}
// //               >
// //                 Pobierz
// //                 {/* <a
// //                   style={{ textDecoration: "none", color: "#d2ab67" }}
// //                   href={cert.url}
// //                   onClick={() =>
// //                     download(
// //                       "https://vccsystem.eu/wp-content/uploads/2021/01/certyfikat-bez-logotypu-scaled.jpg"
// //                     )
// //                   }
// //                   target="_blank"
// //                   rel="noreferrer"
// //                 >
// //                   Pobierz
// //                 </a> */}
// //               </Button>
// //               {/* <Button
// //                 variant="outlined"
// //                 endIcon={<PrintIcon />}
// //                 onClick={handlePrint}
// //               >
// //                 Drukuj
// //               </Button> */}
// //             </Stack>
// //           </Container>
// //         </Box>
// //       )}
// //       {/* <Copyright sx={{ mt: 8, mb: 4 }} /> */}
// //     </main>
// //   );
// // }
